// import wasmUrl from "../../wasm/zig-out/bin/hero_particles.wasm";
const wasmUrl = new URL("/wasm/hero_particles.wasm", import.meta.url).href;

// ─── Раскладка extern struct Particle в main.zig ──────────────────
// 16 × f32 = 64 байта на частицу. Порядок полей должен совпадать
// с объявлением struct в Zig.
const FLOATS_PER_PARTICLE = 16;
const OFFSET = {
  homeX: 0,
  homeY: 1,
  x: 2,
  y: 3,
  vx: 4,
  vy: 5,
  r0: 6,
  r: 7,
  cmykR: 8,
  cmykG: 9,
  cmykB: 10,
  heat: 11,
  growth: 12,
  wobblePhase: 13,
  colorable: 14, // 0.0 / 1.0, в JS не используется
};

// ─── Цвета и константы рендера (совпадают с CONFIG в main.zig) ────
const PAPER = [246, 247, 249]; // #f6f7f9
const COLD = [30, 36, 46];     // #1e242e
const COLD_ALPHA = 0.055;
const HEAT_ALPHA_MAX = 0.88;
const GROW_ALPHA_BOOST = 0.15;
const RADIAL_GRADIENT_THRESHOLD = 1.8; // r > 1.8 → градиент

// ─── Ленивая загрузка WASM ────────────────────────────────────────
// Файл грузится один раз на страницу, даже если initHeroParticles()
// вызвать несколько раз (например, при навигации SPA).
// arrayBuffer + instantiate (а не instantiateStreaming) — работает
// даже при неверном MIME-типе, что типично для php -S в dev-режиме.
let wasmPromise = null;

function loadWasm() {
  if (!wasmPromise) {
    wasmPromise = (async () => {
      const resp = await fetch(wasmUrl);
      if (!resp.ok) {
        throw new Error(
          `hero-particles: не удалось загрузить WASM ` +
            `(HTTP ${resp.status}, ${wasmUrl})`
        );
      }
      const bytes = await resp.arrayBuffer();
      const { instance } = await WebAssembly.instantiate(bytes, {});
      return instance.exports;
    })().catch((err) => {
      // Сбрасываем кэш, чтобы повторный вызов мог попробовать снова
      // (например, после восстановления сети).
      wasmPromise = null;
      throw err;
    });
  }
  return wasmPromise;
}

// ─── Главная функция ─────────────────────────────────────────────
/**
 * @param {object} [options]
 * @param {string} [options.selector="#hero-particles"]
 * @param {number|bigint} [options.seed]  — опционально, для детерминизма
 * @returns {Promise<{start: () => void, stop: () => void, destroy: () => void}>}
 */
export async function initHeroParticles(options = {}) {
  const selector = options.selector || "#hero-particles";
  const canvas = document.querySelector(selector);
  if (!canvas) return null;

  // Защита от повторной инициализации на том же canvas.
  if (canvas.__heroParticlesHandle) {
    return canvas.__heroParticlesHandle;
  }

  // Уважаем настройки доступности — не запускаем анимацию.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return null;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const wasm = await loadWasm();

  // Быстрый fail с понятным сообщением, если экспорты не совпадают
  // (например, при рассинхроне версий JS и WASM).
  const required = [
    "init",
    "update",
    "setViewport",
    "getParticlesPtr",
    "getParticleCount",
    "memory",
  ];
  const missing = required.filter((k) => !(k in wasm));
  if (missing.length) {
    throw new Error(
      `hero-particles: в WASM отсутствуют экспорты: ${missing.join(", ")}`
    );
  }

  // ─── Сид (опционально) ────────────────────────────────────────
  if (options.seed !== undefined && typeof wasm.setSeed === "function") {
    const raw =
      typeof options.seed === "bigint"
        ? options.seed
        : BigInt(Math.floor(options.seed));
    wasm.setSeed(BigInt.asUintN(64, raw));
  }

  // ─── Canvas: DPR + логические размеры ───────────────────────
  let width = 0;
  let height = 0;

  function setupCanvas() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    // Работаем в CSS-пикселях, DPR-скейл — через transform.
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ─── View на линейную память WASM ───────────────────────────
  // После каждого init() (в т.ч. из resize) память может вырасти →
  // старый ArrayBuffer отвяжется, view нужно пересоздать.
  let particleView = null;
  let particleCount = 0;

  function refreshView() {
    particleCount = wasm.getParticleCount();
    const ptr = wasm.getParticlesPtr();
    particleView = new Float32Array(
      wasm.memory.buffer,
      ptr,
      particleCount * FLOATS_PER_PARTICLE
    );
  }

  setupCanvas();
  wasm.init(width, height);
  refreshView();

  // ─── Указатель: мышь + тач ──────────────────────────────────
  const mouse = { x: -9999, y: -9999, active: false, vx: 0, vy: 0 };
  let lastMX = 0;
  let lastMY = 0;

  function movePointer(clientX, clientY) {
    const r = canvas.getBoundingClientRect();
    const x = clientX - r.left;
    const y = clientY - r.top;
    if (!mouse.active) {
      lastMX = x;
      lastMY = y;
      mouse.vx = 0;
      mouse.vy = 0;
    }
    mouse.x = x;
    mouse.y = y;
    mouse.active = true;
  }

  function leavePointer() {
    mouse.active = false;
    mouse.x = -9999;
    mouse.y = -9999;
    mouse.vx = 0;
    mouse.vy = 0;
  }

  function onMouseMove(e) {
    movePointer(e.clientX, e.clientY);
  }

  function onTouchMove(e) {
    if (!e.touches.length) return;
    movePointer(e.touches[0].clientX, e.touches[0].clientY);
  }

  // AbortController упрощает снятие всех слушателей в destroy().
  const ac = new AbortController();
  const { signal } = ac;

  // Слушаем на родителе: контент hero обычно лежит поверх canvas
  // с pointer-events: none, и события доходят до контейнера.
  const hero = canvas.parentElement || canvas;

  hero.addEventListener("mousemove", onMouseMove, { signal });
  hero.addEventListener("mouseleave", leavePointer, { signal });
  hero.addEventListener("touchmove", onTouchMove, {
    passive: true,
    signal,
  });
  hero.addEventListener("touchend", leavePointer, { signal });
  hero.addEventListener("touchcancel", leavePointer, { signal });

  // ─── Рендер ─────────────────────────────────────────────────
  function draw() {
    // Фон
    ctx.fillStyle = `rgb(${PAPER[0]},${PAPER[1]},${PAPER[2]})`;
    ctx.fillRect(0, 0, width, height);

    const data = particleView;
    const n = particleCount;

    // Pass 1: холодные точки — все частицы
    for (let i = 0; i < n; i++) {
      const b = i * FLOATS_PER_PARTICLE;
      const x = data[b + OFFSET.x];
      const y = data[b + OFFSET.y];
      const r = data[b + OFFSET.r];
      const heat = data[b + OFFSET.heat];
      const growth = data[b + OFFSET.growth];

      ctx.globalAlpha = COLD_ALPHA + heat * 0.04 + growth * 0.02;

      if (r > RADIAL_GRADIENT_THRESHOLD) {
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0.0, `rgb(${COLD[0]},${COLD[1]},${COLD[2]})`);
        g.addColorStop(0.55, `rgb(${COLD[0]},${COLD[1]},${COLD[2]})`);
        g.addColorStop(1.0, `rgba(${COLD[0]},${COLD[1]},${COLD[2]},0)`);
        ctx.fillStyle = g;
      } else {
        ctx.fillStyle = `rgb(${COLD[0]},${COLD[1]},${COLD[2]})`;
      }
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Pass 2: CMYK через multiply — только «горячие» частицы
    ctx.globalCompositeOperation = "multiply";
    for (let i = 0; i < n; i++) {
      const b = i * FLOATS_PER_PARTICLE;
      const heat = data[b + OFFSET.heat];
      if (heat < 0.005) continue;

      const cr = data[b + OFFSET.cmykR] | 0;
      const cg = data[b + OFFSET.cmykG] | 0;
      const cb = data[b + OFFSET.cmykB] | 0;
      const growth = data[b + OFFSET.growth];
      const x = data[b + OFFSET.x];
      const y = data[b + OFFSET.y];
      const r = data[b + OFFSET.r];

      ctx.globalAlpha = Math.min(
        1,
        heat * HEAT_ALPHA_MAX * (1 + growth * GROW_ALPHA_BOOST)
      );

      if (r > RADIAL_GRADIENT_THRESHOLD) {
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0.0, `rgb(${cr},${cg},${cb})`);
        g.addColorStop(0.6, `rgb(${cr},${cg},${cb})`);
        g.addColorStop(1.0, `rgba(${cr},${cg},${cb},0)`);
        ctx.fillStyle = g;
      } else {
        ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
      }
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
  }

  // ─── Цикл кадров ────────────────────────────────────────────
  let rafId = null;

  function frame(ts) {
    // Скорость курсора за кадр (клампим, чтобы при телепорте
    // указателя частицы не улетали).
    if (mouse.active) {
      mouse.vx = Math.max(-60, Math.min(60, mouse.x - lastMX));
      mouse.vy = Math.max(-60, Math.min(60, mouse.y - lastMY));
      lastMX = mouse.x;
      lastMY = mouse.y;
    } else {
      mouse.vx = 0;
      mouse.vy = 0;
    }

    // ts (ms from navigation start) передаётся как «t» в WASM:
    // там оно используется только для flow-поля и wobble,
    // которые не зависят от фактического dt между кадрами.
    wasm.update(ts, mouse.x, mouse.y, mouse.active, mouse.vx, mouse.vy);

    // Если resize() вызвал init() — число частиц могло измениться,
    // а буфер памяти — переехать. Пересоздаём view.
    const newCount = wasm.getParticleCount();
    if (newCount !== particleCount) {
      refreshView();
    }

    draw();
    rafId = requestAnimationFrame(frame);
  }

  function start() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(frame);
  }

  function stop() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  // ─── Resize (debounce 150 мс) ───────────────────────────────
  let resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      setupCanvas();
      wasm.setViewport(width, height);
      wasm.init(width, height);
      refreshView();
    }, 150);
  }
  window.addEventListener("resize", onResize, { signal });

  // ─── Пауза при скрытой вкладке ──────────────────────────────
  function onVisibilityChange() {
    if (document.hidden) stop();
    else start();
  }
  document.addEventListener("visibilitychange", onVisibilityChange, {
    signal,
  });

  // ─── Public handle ──────────────────────────────────────────
  const handle = {
    start,
    stop,
    destroy() {
      stop();
      ac.abort(); // снимает все слушатели, добавленные с { signal }
      clearTimeout(resizeTimer);
      delete canvas.__heroParticlesHandle;
    },
  };
  canvas.__heroParticlesHandle = handle;

  start();
  return handle;
}
